"use client";

import * as React from "react";
import { type VariantProps } from "class-variance-authority";
import { ToggleGroup as ToggleGroupPrimitive } from "radix-ui";

import { cn } from "../lib/cn";
import { nx, NX_RING_CLIP, type NxSize } from "../lib/onyx-material";
import { toggleVariants } from "./toggle";

/** The shared springy offset-key bounce — every interactive control rides it. */
const BOUNCE = "transition-transform duration-150 ease-[cubic-bezier(0.34,1.45,0.6,1)]";

const ToggleGroupContext = React.createContext<
  VariantProps<typeof toggleVariants> & {
    spacing?: number;
  }
>({
  size: "default",
  variant: "default",
  spacing: 0,
});

/**
 * Group of `<ToggleGroupItem>` buttons in the Onyx notch language — a segmented
 * selector. At `spacing={0}` (default) the items fuse into one rounded segmented
 * control; at `spacing>0` each becomes a standalone notched chip. The pressed one
 * reads as the accent segment.
 *
 * Pass `type="single"` for radio-style (only one pressed at a time)
 * or `type="multiple"` for checkbox-style (any subset pressed).
 */
function ToggleGroup({
  className,
  variant,
  size,
  spacing = 0,
  children,
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Root> &
  VariantProps<typeof toggleVariants> & {
    spacing?: number;
  }) {
  return (
    <ToggleGroupPrimitive.Root
      data-slot="toggle-group"
      data-variant={variant}
      data-size={size}
      data-spacing={spacing}
      style={{ "--gap": spacing } as React.CSSProperties}
      className={cn(
        "group/toggle-group flex w-fit items-center gap-[--spacing(var(--gap))] rounded-md data-[spacing=default]:data-[variant=outline]:shadow-xs",
        className,
      )}
      {...props}
    >
      <ToggleGroupContext.Provider value={{ variant, size, spacing }}>
        {children}
      </ToggleGroupContext.Provider>
    </ToggleGroupPrimitive.Root>
  );
}

/**
 * Single button inside a `<ToggleGroup>`. Inherits `variant` and
 * `size` from the group via context.
 *
 * At the group's default `spacing={0}` the items FUSE into one segmented control
 * (joined rounded segments), so a zero-gap group reads as a single surface. Give
 * the group `spacing>0` and each item becomes a standalone chip in the hex-notch
 * silhouette (`nx-t`) with a continuous `nx-rim` keyline (a real border is sliced
 * at the notch, #158). Either way they share the springy press — a 1px press-DOWN
 * on `:active` with the overshoot bounce — and the pressed (`data-state=on`)
 * segment reads as the accent fill. Flat faces, not raised keys: no travel block.
 */
function ToggleGroupItem({
  className,
  children,
  variant,
  size,
  notch = "t",
  ...props
}: React.ComponentProps<typeof ToggleGroupPrimitive.Item> &
  VariantProps<typeof toggleVariants> & {
    /** Notch chamfer size. Default `t` (chip). */
    notch?: NxSize;
  }) {
  const context = React.useContext(ToggleGroupContext);
  const variantResolved = context.variant || variant;
  const sizeResolved = context.size || size;
  // spacing=0 → one FUSED segmented control: joined rounded segments, NOT N
  // individually-notched chips (which would read as N separate controls and leave
  // the old rounded-*/border-l joining classes dead under the clip-path). spacing>0
  // → each item is a standalone notched chip.
  const segmented = (context.spacing ?? 0) === 0;

  return (
    <ToggleGroupPrimitive.Item
      data-slot="toggle-group-item"
      data-variant={variantResolved}
      data-size={sizeResolved}
      data-spacing={context.spacing}
      className={cn(
        toggleVariants({ variant: variantResolved, size: sizeResolved }),
        BOUNCE,
        "active:translate-y-px disabled:active:translate-y-0",
        "relative w-auto min-w-0 shrink-0 px-3 focus:z-10 focus-visible:z-10",
        segmented
          ? // Fused segments: joined rounded ends + a shared divider for outline.
            "rounded-none first:rounded-l-md last:rounded-r-md data-[variant=outline]:border data-[variant=outline]:border-input data-[variant=outline]:border-l-0 data-[variant=outline]:first:border-l"
          : // Standalone chip: the hex-notch silhouette.
            nx[notch],
        className,
      )}
      {...props}
    >
      {/* A standalone (spaced) chip carries the notched keyline ring; a fused
          segment does not — it joins its neighbours as one surface. */}
      {!segmented && (
        <span
          aria-hidden
          className="nx-rim pointer-events-none absolute inset-0"
          style={{ "--nx-rim": "hsl(var(--border))", clipPath: NX_RING_CLIP[notch] } as React.CSSProperties}
        />
      )}
      {children}
    </ToggleGroupPrimitive.Item>
  );
}

export { ToggleGroup, ToggleGroupItem };
